/**
 * Pure helper for the schedule-confirmation modal's duration validation.
 *
 * Extracted from CalendarView so the duration-challenge feature flag behaviour
 * is unit-testable and shared between the status banner and the footer button.
 */
import { differenceInCalendarDays, parseISO } from 'date-fns';
export interface DurationStatusInput {
  /** Estimated duration for the job, in minutes. */
  durationMinutes: number;
  /** Selected slot start. */
  start: Date;
  /** Selected slot end. */
  end: Date;
  /**
   * Whether the duration challenge is enabled for the current client. When
   * disabled, exceeding the estimate is never flagged — the job is scheduled
   * regardless of the selected duration.
   */
  durationChallengeEnabled: boolean;
  /**
   * The current moment. When provided, a start earlier than this is flagged as
   * `isInPast` and makes the slot invalid — jobs cannot be scheduled in the
   * past. When omitted, the past check is skipped entirely.
   */
  now?: Date;
}

export interface DurationStatus {
  /** Selected duration in minutes. */
  diffMinutes: number;
  /** End is at or before start. */
  isBeforeStart: boolean;
  /** End is before the minimum (estimated) duration. */
  isBelowDuration: boolean;
  /** Selected duration exceeds the estimate AND the challenge is enabled. */
  isAboveDuration: boolean;
  /** Start is before `now` — the job would be scheduled in the past. */
  isInPast: boolean;
  /** All checks pass — safe to schedule directly. */
  isValid: boolean;
}

/**
 * Number of distinct calendar days a scheduled job touches, for the
 * "(spans N days)" hint in the schedule modal.
 *
 * This is a *calendar* measure, not a duration measure: a 1-day (24h) job
 * starting 08:00 on the 16th and ending 08:00 on the 17th touches two calendar
 * days, so it spans 2 — even though its length is exactly one day. The previous
 * `Math.ceil(durationMinutes / 1440)` calc reported 1 for that case (WRK-5707).
 *
 * A job whose end time is exactly midnight (`00:00`) finishes on the day
 * boundary and does not touch the trailing day, so it is not counted.
 *
 * Dates are the `yyyy-MM-dd` wall-clock strings shown in the modal.
 *
 * @param startDate Start date, `yyyy-MM-dd`.
 * @param endDate   End date, `yyyy-MM-dd`.
 * @param endTime   End time, `HH:mm`.
 */
export function computeSpannedDays(startDate: string, endDate: string, endTime: string): number {
  const calendarDaysCrossed = differenceInCalendarDays(parseISO(endDate), parseISO(startDate));
  const endsAtMidnight = endTime === '00:00';
  return Math.max(1, calendarDaysCrossed + (endsAtMidnight ? 0 : 1));
}

export function computeDurationStatus({
  durationMinutes,
  start,
  end,
  durationChallengeEnabled,
  now,
}: DurationStatusInput): DurationStatus {
  const minEndDt = new Date(start.getTime() + durationMinutes * 60 * 1000);
  const diffMs = end.getTime() - start.getTime();
  const diffMinutes = Math.round(diffMs / (1000 * 60));

  const isBeforeStart = diffMs <= 0;
  const isBelowDuration = end < minEndDt;
  const isAboveDuration =
    durationChallengeEnabled && !isBeforeStart && diffMinutes > durationMinutes;
  const isInPast = now !== undefined && start.getTime() < now.getTime();
  const isValid = !isBeforeStart && !isBelowDuration && !isAboveDuration && !isInPast;

  return { diffMinutes, isBeforeStart, isBelowDuration, isAboveDuration, isInPast, isValid };
}
